import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, Medal, Star, LockSimple } from '@phosphor-icons/react';

const PrivateRow = ({ rank, icon }) => (
  <div className={`p-3 border border-[#E4E4E7] bg-white ${rank <= 3 ? 'border-l-2' : ''}`} data-testid={`leaderboard-item-${rank}`}>
    <div className="flex items-center gap-3">
      <div className="flex items-center justify-center w-8">
        {icon || <span className="text-sm font-mono font-bold text-[#71717A]">{rank}</span>}
      </div>
      <div className="h-10 w-10 bg-[#FAFAFA] border border-[#E4E4E7] flex items-center justify-center rounded-full">
        <LockSimple size={16} weight="fill" className="text-[#71717A]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#71717A]" data-testid={`leaderboard-private-${rank}`}>Private player</p>
        <p className="text-xs font-mono text-[#A1A1AA]">Stats hidden</p>
      </div>
    </div>
  </div>
);

export const LeaderboardPanel = ({ leaderboard, currentUser, onViewProfile }) => {
  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy size={18} weight="fill" className="text-[#0047FF]" />;
    if (rank === 2) return <Medal size={18} weight="fill" className="text-[#0047FF]" />;
    if (rank === 3) return <Star size={18} weight="fill" className="text-[#0047FF]" />;
    return null;
  };

  return (
    <div className="w-96 bg-white flex flex-col" data-testid="leaderboard-panel">
      <div className="p-6 border-b border-[#E4E4E7]">
        <h2 className="font-heading text-xl font-bold tracking-tight text-[#09090B]">
          Leaderboard
        </h2>
        <p className="text-sm text-[#71717A] mt-1">Top players worldwide</p>
      </div>

      <ScrollArea className="flex-1 scrollbar-hide">
        <div className="p-6">
          <div className="space-y-1">
            {leaderboard.map((player, index) => {
              const rank = index + 1;
              if (player.is_private) return <PrivateRow key={player.id} rank={rank} icon={getRankIcon(rank)} />;
              const isCurrentUser = player.id === currentUser.id;
              
              return (
                <div
                  key={player.id}
                  className={`p-3 border border-[#E4E4E7] transition-colors ${
                    isCurrentUser
                      ? 'bg-[#0047FF] text-white border-[#0047FF]'
                      : 'bg-white hover:bg-[#FAFAFA]'
                  } ${
                    rank <= 3 ? 'border-l-2' : ''
                  }`}
                  data-testid={`leaderboard-item-${rank}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8">
                      {getRankIcon(rank) || (
                        <span className={`text-sm font-mono font-bold ${
                          isCurrentUser ? 'text-white' : 'text-[#71717A]'
                        }`}>
                          {rank}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => onViewProfile(player.id)}
                      className="hover:opacity-80 transition-opacity"
                      data-testid={`leaderboard-avatar-${rank}`}
                    >
                      <Avatar className="h-10 w-10 cursor-pointer">
                        <AvatarImage src={player.avatar} alt={player.username} />
                        <AvatarFallback>{player.username[0]}</AvatarFallback>
                      </Avatar>
                    </button>
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => onViewProfile(player.id)}
                        className={`text-sm font-medium truncate block text-left hover:underline ${
                          isCurrentUser ? 'text-white' : rank <= 3 ? 'text-[#0047FF]' : 'text-[#09090B]'
                        }`}
                      >
                        {player.username}
                        {isCurrentUser && ' (You)'}
                      </button>
                      <p className={`text-xs font-mono ${
                        isCurrentUser ? 'text-white/80' : 'text-[#71717A]'
                      }`}>
                        {player.score.toLocaleString()} points
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {leaderboard.length === 0 && (
            <div className="text-center py-12">
              <Trophy size={48} weight="regular" className="mx-auto text-[#E4E4E7] mb-3" />
              <p className="text-sm text-[#71717A]">No players yet</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};